import React, { useRef, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatNumber } from 'utils/formatter';
import { DividendProps } from 'pages/DividendPrediction';
import { getSummaryDividendHaito } from './DividendTransition';
import { DividendListProps } from './DividendClassification';
import { commaFormat } from '../new-report/utils/formatter';
import { LineChartLegend, marker } from '../utils/chartUtils';

// カラムチャート
export const DividendColumnChart = ({
  dividendData,
  selectedDate,
  isTaxDeduction,
}: {
  dividendData: DividendProps[];
  selectedDate: string;
  isTaxDeduction: boolean;
}) => {
  const chartData = useMemo(() => {
    const predictionDividendData = dividendData.filter(
      data => !data.unreleased
    );
    const notAnnouncedDividendData = dividendData.filter(
      data => data.unreleased
    );

    const displayPeriodMonth = 12;
    const targetPeriodList = [...Array(displayPeriodMonth)].map((_, i) => {
      const month = (i + 1).toString();
      const targetDate = `${selectedDate}${month.padStart(2, '0')}`;
      return targetDate;
    });

    return targetPeriodList.map(period => {
      const predictionTargetData = predictionDividendData.filter(
        data => data.haitoYm === period
      );
      const predictionTotalHaito = getSummaryDividendHaito(
        predictionTargetData,
        isTaxDeduction
      );

      const notAnnouncedTargetData = notAnnouncedDividendData.filter(
        data => data.haitoYm === period
      );
      const notAnnouncedTotalHaito = getSummaryDividendHaito(
        notAnnouncedTargetData,
        isTaxDeduction
      );

      return {
        predictionDividendData: predictionTotalHaito,
        notAnnouncedDividendData: notAnnouncedTotalHaito,
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, dividendData, isTaxDeduction]);

  const options = {
    chart: {
      type: 'column',
      animation: {
        duration: 0,
      },
      height: 220,
      backgroundColor: '#ffffff',
    },
    credits: {
      enabled: false,
    },
    exporting: { enabled: false },
    rangeSelector: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      labels: { y: 22 },
      tickInterval: 2,
      tickWidth: 1,
      tickLength: 10,
      categories: [
        '1月',
        '2月',
        '3月',
        '4月',
        '5月',
        '6月',
        '7月',
        '8月',
        '9月',
        '10月',
        '11月',
        '12月',
      ],
    },
    yAxis: {
      labels: {
        align: 'left',
        x: 5,
        y: 4,
        formatter: function(): string {
          return typeof this.value === 'number'
            ? commaFormat(this.value / 10000, { numberOfDecimals: 1 })
            : '';
        },
      },
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        x: 5,
        y: 3,
      },
      tickAmount: 4,
      showLastLabel: false,
      opposite: true,
      alignTicks: false,
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderRadius: 2,
      },
      series: {
        turboThreshold: 15000,
      },
    },
    navigator: {
      enabled: false,
      maskFill: 'rgba(203, 213, 225, 0.6)',
    },
    title: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: 0,
      y: -15,
      margin: 15,
      itemStyle: { fontSize: '10px', fontWeight: 'normal' },
      maxHeight: 54,
      reversed: true,
      className: 'override-highcharts-legend common-margin-top',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          return renderToStaticMarkup(
            <LineChartLegend color={color}>{name}</LineChartLegend>
          );
        }
        return '';
      },
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      formatter: function(): string {
        const Tooltip = ({
          num,
          children,
        }: {
          num: number;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          const date_ = num.toString();
          const date = date_.slice(0, -1);
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {selectedDate}/{date}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          const num = Math.ceil(value / 100) * 100;
          const label = `${commaFormat(num / 10000, {
            numberOfDecimals: 2,
          })}万円`;

          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {label}
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip num={this.x}>
            {(this.points ?? []).map((point, key) => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
                key={key}
              />
            ))}
          </Tooltip>
        );
      },
      shared: true,
    },
    series: [
      {
        name: '未発表',
        data: chartData.map(data => data.notAnnouncedDividendData),
        color: '#C9E1FF',
        type: 'column',
      },
      {
        name: '予想',
        data: chartData.map(data => data.predictionDividendData),
        color: '#60A5FA',
        type: 'column',
      },
    ],
  } as Highcharts.Options;

  const ref = useRef<HighchartsReact.RefObject>(null);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
  );
};

// パイチャート
type PortfolioRatioChartDatum = {
  key: string;
  ratio: number;
};

export const dataToSeries = (
  data: PortfolioRatioChartDatum[]
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      type: 'pie',
      data: data.map(
        (datum, index): Highcharts.PointOptionsObject => {
          const color = colorList[index];
          const label = datum.key;
          return {
            color,
            name: label,
            y: datum.ratio,
          };
        }
      ),
      dataLabels: {
        enabled: true,
        distance: '30%',
        style: {
          color: '#334155',
          textOutline: 'none',
        },
      },
      minSize: 180,
      center: ['50%', '50%'],
    },
  ];
};

export const DividendPieChartNoData = () => {
  return (
    <div className="flex">
      <img
        src="/static/images/dividend/dividend_pie_chart_no_data.png"
        alt="パイチャートのイメージ"
        className="mx-auto"
        height="255"
        width="255"
      />
    </div>
  );
};

type PieChartDataProps = {
  key: string;
  ratio: number;
};

export const DividendPieChart = ({
  dividendList,
  selectedDate,
  isTaxDeduction,
}: {
  dividendList: DividendListProps[];
  selectedDate: string;
  isTaxDeduction: boolean;
}) => {
  const chartData = useMemo(() => {
    const summaryDividendData = dividendList.map(list => {
      return list.items.reduce(
        (acc, current) => {
          const haito = isTaxDeduction
            ? current.taxExcludedHaito
            : current.haito;
          return {
            symbol: acc.symbol,
            symbolName: acc.symbolName,
            haito: acc.haito + haito,
          };
        },
        {
          symbol: list.symbol,
          symbolName: list.symbolName,
          haito: list.items[0].haito,
        }
      );
    });

    const sortedDividendData = summaryDividendData.sort((a, b) => {
      return a.haito < b.haito ? 1 : -1;
    });
    const totalDividend = sortedDividendData.reduce(
      (acc, v) => acc + v.haito,
      0
    );

    const displayItemIndex = 19;
    if (sortedDividendData.length < displayItemIndex) {
      const chartData = sortedDividendData.map(item => {
        return { key: item.symbolName, ratio: item.haito / totalDividend };
      });

      return chartData;
    } else {
      const targetDividendData = sortedDividendData.filter(
        (_, index) => index < displayItemIndex
      );
      const displayDividenChartData = targetDividendData.map(item => {
        return { key: item.symbolName, ratio: item.haito / totalDividend };
      });

      const otherDividendData = sortedDividendData.filter(
        (_, index) => index >= displayItemIndex
      );
      const otherTotalDividend = otherDividendData.reduce(
        (acc, v) => acc + v.haito,
        0
      );
      const otherIndex = sortedDividendData.length - displayItemIndex;
      const otherDividendChartData = {
        key: `その他（${formatNumber(otherIndex, {})}件）`,
        ratio: otherTotalDividend / totalDividend,
      };
      const chartData = displayDividenChartData.concat(
        otherDividendChartData
      ) as PieChartDataProps[];

      return chartData;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividendList, selectedDate, isTaxDeduction]);

  const series = dataToSeries(chartData.filter(datum => datum.ratio >= 0));

  const options = {
    exporting: { enabled: false },
    credits: { enabled: false },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemMarginTop: 10,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '10px',
        color: '#ffffff',
      },
    },
    chart: {
      type: 'column',
      height: 255,
    },
    tooltip: {
      outside: true,
      formatter(): string {
        return `${this.point.name}: ${commaFormat(this.point.percentage ?? 0, {
          numberOfDecimals: 1,
        })}%`;
      },
    },
    plotOptions: {
      series: {
        ...marker,
        borderColor: '#ffffff',
        borderWidth: 2,
        turboThreshold: 15000,
      },
      pie: {
        innerSize: '50%',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>',
          style: {
            fontWeight: 'normal',
          },
        },
      },
    },
    series,
  } as Highcharts.Options;

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};

const colorList = [
  '#7C6BE5',
  '#A1E56B',
  '#E56BB5',
  '#31C6CF',
  '#2DB897',
  '#F1894F',
  '#CF3144',
  '#3E739A',
  '#6D7E98',
  '#A17592',
  '#37C681',
  '#3196CF',
  '#BA6FD4',
  '#CF4D31',
  '#FFC738',
  '#7E81CD',
  '#D48888',
  '#CF317D',
  '#548989',
  '#9F9E95',
];
